package pl.nickers.match3.ui

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.ui.input.pointer.PointerEvent
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.input.pointer.isSecondaryPressed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.layout.offset
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.Layout
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import kotlinproject.composeapp.generated.resources.Res
import kotlinproject.composeapp.generated.resources.board_frame
import kotlinproject.composeapp.generated.resources.cell_dark
import kotlinproject.composeapp.generated.resources.cell_light
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.imageResource
import org.jetbrains.compose.resources.painterResource
import androidx.compose.ui.util.lerp
import pl.nickers.match3.EFFECTS_DURATION_MS
import pl.nickers.match3.FUSE_DURATION_MS
import pl.nickers.match3.BombPowerUpFlightUi
import pl.nickers.match3.GameState
import pl.nickers.match3.FamiliarFlightUi
import pl.nickers.match3.GoalJellyFlightUi
import pl.nickers.match3.GridPos
import pl.nickers.match3.InputMode
import pl.nickers.match3.PowerUpInfo
import pl.nickers.match3.JellyCell
import pl.nickers.match3.ecs.TutorialStep
import pl.nickers.match3.LASER_DURATION_MS
import pl.nickers.match3.SWAP_DURATION_MS
import pl.nickers.match3.FALL_STEP_DURATION_MS
import pl.nickers.match3.SWIRL_FADE_DURATION_MS
import pl.nickers.match3.SWIRL_VACUUM_DURATION_MS
import pl.nickers.match3.decodeToImageBitmap
import pl.nickers.match3.ecs.LaserDirection
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi
import kotlin.math.abs
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt

internal val GRID_PADDING = 24.dp
private const val GAP_FRACTION = 0f

/**
 * board_frame.png is 1024px with a transparent 812px opening (106px wooden band +
 * margin per side). The frame is stretched so its opening matches the board rect,
 * and the cell size is shrunk by this factor so band + board still fit the screen.
 */
private const val FRAME_OPENING_FRACTION = 812f / 1024f

/** Fraction of cell size the pointer must travel before a drag is recognised. */
private const val DRAG_THRESHOLD_FRACTION = 0.25f

private enum class DragDirection { UP, DOWN, LEFT, RIGHT }

private data class DragInfo(
    val sourcePos: GridPos,
    val direction: DragDirection,
    val targetPos: GridPos,
)

@OptIn(ExperimentalEncodingApi::class)
@Composable
internal fun GameGrid(
    state: GameState,
    padding: Dp,
    onCellClick: (GridPos) -> Unit,
    onCellSecondaryAction: (GridPos) -> Unit = {},
    onDragSwap: (from: GridPos, to: GridPos) -> Unit,
    onRowBombTarget: (GridPos) -> Unit = {},
    onTargetedRemoveTarget: (GridPos) -> Unit = {},
    onTeleportLinkingTarget: (GridPos) -> Unit = {},
    onSwapFinished: () -> Unit,
    onFallFinished: () -> Unit,
    onFuseFinished: () -> Unit,
    onLaserFinished: () -> Unit,
    onEffectsFinished: () -> Unit,
    onSwirlVacuumFinished: () -> Unit,
    backgroundImageBase64: String? = null,
    flightAnchors: FlightAnchorRegistry? = null,
    bombPowerUpFlight: BombPowerUpFlightUi? = null,
    goalJellyFlights: List<GoalJellyFlightUi> = emptyList(),
    familiarFlights: List<FamiliarFlightUi> = emptyList(),
    powerUps: List<PowerUpInfo> = emptyList(),
    onClearBombPowerUpFlight: () -> Unit = {},
    onDismissGoalJellyFlight: (Int) -> Unit = {},
    onDismissFamiliarFlight: (Int) -> Unit = {},
) {
    val inputMode = state.inputMode
    val grid = state.grid
    val swappingCells = state.swappingCells
    val fallingCells = state.fallingCells
    val activatingLasers = state.activatingLasers
    val explodingBombs = state.explodingBombs
    val fusedBombs = state.fusedBombs
    val swirlVacuumCells = state.swirlVacuumCells
    val teleportingCells = state.teleportingCells
    val teleportLinks = state.teleportLinks
    val teleportLinkingDestination = state.teleportLinkingDestination
    val tutorial = state.tutorial

    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .then(if (flightAnchors != null) Modifier.flightRootAnchor(flightAnchors) else Modifier),
        contentAlignment = Alignment.Center,
    ) {
        val density = LocalDensity.current
        val paddingPx = with(density) { padding.toPx() }
        // Board dimensions come from the rendered grid so any rectangular size fits.
        val rows = grid.size.coerceAtLeast(1)
        val cols = grid.firstOrNull()?.size?.coerceAtLeast(1) ?: 1
        val availableW = constraints.maxWidth.toFloat() - paddingPx * 2
        val availableH = constraints.maxHeight.toFloat() - paddingPx * 2
        // Pick the largest cell size that lets the whole board (width AND height) fit,
        // leaving room for the wooden frame band around the board.
        val cellPx = min(
            availableW * FRAME_OPENING_FRACTION / (cols + GAP_FRACTION * (cols - 1)),
            availableH * FRAME_OPENING_FRACTION / (rows + GAP_FRACTION * (rows - 1)),
        )
        val gapPx = cellPx * GAP_FRACTION
        val stride = cellPx + gapPx
        val cellDp = with(density) { cellPx.toDp() }
        val gridWpx = cols * cellPx + (cols - 1) * gapPx
        val gridHpx = rows * cellPx + (rows - 1) * gapPx
        val gridWidthDp = with(density) { gridWpx.toDp() }
        val gridHeightDp = with(density) { gridHpx.toDp() }
        val swapCompletionDispatched = remember(swappingCells) { mutableStateOf(false) }
        val completedSwapIds = remember(swappingCells) { mutableSetOf<Int>() }

        var dragState by remember { mutableStateOf<DragInfo?>(null) }

        LaunchedEffect(fallingCells) {
            if (fallingCells.isNotEmpty()) {
                delay(FALL_STEP_DURATION_MS.toLong())
                onFallFinished()
            }
        }

        val swirlFadeAlpha = remember { Animatable(1f) }
        val swirlFlyProgress = remember { Animatable(0f) }
        LaunchedEffect(swirlVacuumCells.keys) {
            if (swirlVacuumCells.isEmpty()) {
                swirlFadeAlpha.snapTo(1f)
                swirlFlyProgress.snapTo(0f)
                return@LaunchedEffect
            }
            swirlFadeAlpha.snapTo(1f)
            swirlFlyProgress.snapTo(0f)
            // Phase 1: fade all target cells to 90% opacity
            swirlFadeAlpha.animateTo(0.9f, animationSpec = tween(durationMillis = SWIRL_FADE_DURATION_MS))
            // Phase 2: fly toward swirl + shrink to 10% of original size
            swirlFlyProgress.animateTo(1f, animationSpec = tween(durationMillis = SWIRL_VACUUM_DURATION_MS))
            onSwirlVacuumFinished()
        }

        val backgroundBitmap: ImageBitmap? = remember(backgroundImageBase64) {
            backgroundImageBase64?.let { base64 ->
                runCatching {
                    decodeToImageBitmap(Base64.decode(base64))
                }.getOrNull()
            }
        }

        // Wooden frame around the board: stretched so its transparent opening matches
        // the board rect exactly. Both this Image and the grid Box are centered, so
        // they stay aligned for any board size.
        Image(
            painter = painterResource(Res.drawable.board_frame),
            contentDescription = null,
            modifier = Modifier.size(
                width = with(density) { (gridWpx / FRAME_OPENING_FRACTION).toDp() },
                height = with(density) { (gridHpx / FRAME_OPENING_FRACTION).toDp() },
            ),
            contentScale = ContentScale.FillBounds,
        )

        // Render the background image as the very first (lowest Z-order) child so
        // that the cell tiles and overlays appear on top of it.
        if (backgroundBitmap != null) {
            Canvas(modifier = Modifier.fillMaxSize()) {
                val cx = size.width / 2
                val cy = size.height / 2
                val gL = cx - gridWpx / 2
                val gT = cy - gridHpx / 2
                drawImage(
                    image = backgroundBitmap,
                    dstOffset = IntOffset(gL.toInt(), gT.toInt()),
                    dstSize = IntSize(gridWpx.toInt(), gridHpx.toInt()),
                )
            }
        }


        Box(
            modifier = Modifier
                .size(width = gridWidthDp, height = gridHeightDp)
                .then(if (flightAnchors != null) Modifier.flightGridAnchor(flightAnchors) else Modifier)
                .clipToBounds()
                .pointerInput(grid, onCellSecondaryAction, cellPx, inputMode) {
                    // cellPx is a key: block restarts on resize, ensuring correct coordinates.
                    val localStride = cellPx * (1f + GAP_FRACTION)
                    val threshold = cellPx * DRAG_THRESHOLD_FRACTION
                    coroutineScope {
                    val gestureScope = this
                    awaitEachGesture {

                        // Capture the full PointerEvent so we can inspect the button.
                        var pressEvent: PointerEvent
                        do { pressEvent = awaitPointerEvent() }
                        while (!pressEvent.changes.any { !it.previousPressed && it.pressed })
                        val down = pressEvent.changes.first { !it.previousPressed && it.pressed }

                        val startX = down.position.x
                        val startY = down.position.y
                        val col = (startX / localStride).toInt().coerceIn(0, cols - 1)
                        val row = (startY / localStride).toInt().coerceIn(0, rows - 1)
                        val startPos = GridPos(row, col)
                        val pointerId = down.id

                        // Right-click: fire secondary action and absorb until release.
                        if (pressEvent.buttons.isSecondaryPressed) {
                            down.consume()
                            onCellSecondaryAction(startPos)
                            while (true) {
                                val event = awaitPointerEvent()
                                event.changes.forEach { it.consume() }
                                if (event.changes.none { it.pressed }) break
                            }
                            return@awaitEachGesture
                        }

                        val sourceCell = grid.getOrNull(row)?.getOrNull(col)
                        // Drag is disabled in non-swap modes so the user can't accidentally
                        // consume an armed power-up by swiping.
                        val sourceDraggable = inputMode == InputMode.SWAP &&
                            sourceCell?.isDraggable == true && sourceCell?.isEmpty != true

                        var everExceededThreshold = false
                        var longPressTriggered = false

                        // Launch long press timer only for touch input; mouse uses right-click instead.
                        val longPressJob = if (down.type == PointerType.Touch) gestureScope.launch {
                            delay(viewConfiguration.longPressTimeoutMillis)
                            longPressTriggered = true
                            onCellSecondaryAction(startPos)
                        } else null

                        try {
                            while (true) {
                                val event = awaitPointerEvent()
                                val change = event.changes.find { it.id == pointerId } ?: break

                                if (!change.pressed) {
                                    longPressJob?.cancel()
                                    val currentDrag = dragState
                                    if (currentDrag != null) {
                                        onDragSwap(currentDrag.sourcePos, currentDrag.targetPos)
                                    } else if (!everExceededThreshold && !longPressTriggered) {
                                        when (inputMode) {
                                            InputMode.SWAP -> onCellClick(startPos)
                                            InputMode.ROW_BOMB_TARGETING -> onRowBombTarget(startPos)
                                            InputMode.TARGETED_REMOVE_TARGETING -> onTargetedRemoveTarget(startPos)
                                            InputMode.TELEPORT_LINKING -> onTeleportLinkingTarget(startPos)
                                        }
                                    }
                                    dragState = null
                                    change.consume()
                                    break
                                }

                                if (!sourceDraggable) {
                                    change.consume()
                                    continue
                                }

                                val dx = change.position.x - startX
                                val dy = change.position.y - startY

                                if (abs(dx) > threshold || abs(dy) > threshold) {
                                    longPressJob?.cancel()
                                    everExceededThreshold = true
                                    val direction = if (abs(dx) > abs(dy)) {
                                        if (dx > 0) DragDirection.RIGHT else DragDirection.LEFT
                                    } else {
                                        if (dy > 0) DragDirection.DOWN else DragDirection.UP
                                    }
                                    val target = when (direction) {
                                        DragDirection.UP -> GridPos(row - 1, col)
                                        DragDirection.DOWN -> GridPos(row + 1, col)
                                        DragDirection.LEFT -> GridPos(row, col - 1)
                                        DragDirection.RIGHT -> GridPos(row, col + 1)
                                    }
                                    if (target.row in 0 until rows && target.col in 0 until cols) {
                                        val targetCell = grid.getOrNull(target.row)?.getOrNull(target.col)
                                        if (sourceCell?.isIceCube != true && targetCell?.isDraggable == true && targetCell?.isEmpty != true) {
                                            dragState = DragInfo(startPos, direction, target)
                                        } else {
                                            dragState = null
                                        }
                                    } else {
                                        dragState = null
                                    }
                                } else {
                                    dragState = null
                                }

                                change.consume()
                            }
                        } finally {
                            longPressJob?.cancel()
                        }
                    }
                    } // coroutineScope
                }
        ) {
            // Parchment tile backgrounds (light/dark checkerboard) for accessible
            // (non-hole) cells, plus E5 terrain floors (drawn under the pieces,
            // which render in the Layout below).
            val cellLightTile = imageResource(Res.drawable.cell_light)
            val cellDarkTile = imageResource(Res.drawable.cell_dark)
            Canvas(modifier = Modifier.fillMaxSize()) {
                grid.forEachIndexed { row, rowList ->
                    rowList.forEachIndexed { col, cell ->
                        if (!cell.isHole) {
                            drawImage(
                                image = if ((row + col) % 2 == 0) cellLightTile else cellDarkTile,
                                dstOffset = IntOffset((col * stride).roundToInt(), (row * stride).roundToInt()),
                                dstSize = IntSize(cellPx.roundToInt(), cellPx.roundToInt()),
                            )
                            if (cell.isVoidPuddle) {
                                // Dark inky spill filling most of the cell floor.
                                drawOval(
                                    color = Color(0xCC14102B),
                                    topLeft = Offset(col * stride + cellPx * 0.06f, row * stride + cellPx * 0.12f),
                                    size = Size(cellPx * 0.88f, cellPx * 0.76f),
                                )
                                drawOval(
                                    color = Color(0xFF3D2C66),
                                    topLeft = Offset(col * stride + cellPx * 0.06f, row * stride + cellPx * 0.12f),
                                    size = Size(cellPx * 0.88f, cellPx * 0.76f),
                                    style = Stroke(width = cellPx * 0.04f),
                                )
                            }
                            if (cell.isPurifiedRune) {
                                // Glowing teal ring on the cell floor.
                                val center = Offset(col * stride + cellPx / 2f, row * stride + cellPx / 2f)
                                drawCircle(
                                    color = Color(0x5530E0C0),
                                    radius = cellPx * 0.40f,
                                    center = center,
                                )
                                drawCircle(
                                    color = Color(0xCC30E0C0),
                                    radius = cellPx * 0.40f,
                                    center = center,
                                    style = Stroke(width = cellPx * 0.06f),
                                )
                            }
                        }
                    }
                }
            }

            // Static teleport-link decorations: a thin "portal mouth" ellipse at the
            // bottom of each source cell and at the top of each destination cell.
            if (teleportLinks.isNotEmpty() || teleportLinkingDestination != null) {
                val ellipseColor = Color(0xCC7E57C2)
                val ellipseHeightPx = with(density) { 4.dp.toPx() }
                val ellipseWidthFraction = 0.85f
                Canvas(modifier = Modifier.fillMaxSize()) {
                    teleportLinks.forEach { link ->
                        val srcCenterX = link.source.col * stride + cellPx / 2f
                        val srcBottomY = link.source.row * stride + cellPx
                        val dstCenterX = link.destination.col * stride + cellPx / 2f
                        val dstTopY = link.destination.row * stride
                        val w = cellPx * ellipseWidthFraction
                        drawOval(
                            color = ellipseColor,
                            topLeft = Offset(srcCenterX - w / 2f, srcBottomY - ellipseHeightPx / 2f),
                            size = Size(w, ellipseHeightPx),
                        )
                        drawOval(
                            color = ellipseColor,
                            topLeft = Offset(dstCenterX - w / 2f, dstTopY - ellipseHeightPx / 2f),
                            size = Size(w, ellipseHeightPx),
                        )
                    }
                    if (teleportLinkingDestination != null) {
                        val destCenterX = teleportLinkingDestination.col * stride + cellPx / 2f
                        val destTopY = teleportLinkingDestination.row * stride
                        val w = cellPx * ellipseWidthFraction
                        drawOval(
                            color = Color(0xCCFFC107),
                            topLeft = Offset(destCenterX - w / 2f, destTopY - ellipseHeightPx / 2f),
                            size = Size(w, ellipseHeightPx),
                        )
                        // Highlight the destination cell so the user knows what they're linking.
                        drawRect(
                            color = Color(0x33FFC107),
                            topLeft = Offset(
                                teleportLinkingDestination.col * stride,
                                teleportLinkingDestination.row * stride,
                            ),
                            size = Size(cellPx, cellPx),
                            style = Stroke(width = cellPx * 0.06f),
                        )
                    }
                }
            }

            Layout(
                content = {
                    grid.forEachIndexed { row, rowList ->
                        rowList.forEachIndexed { col, cell ->
                            key(row, col, cell.id) {
                                val pos = GridPos(row, col)
                                val isSelected = state.selected == pos
                                val swapInfo = swappingCells[cell.id]
                                val isSwapping = swapInfo != null
                                val fallInfo = fallingCells[cell.id]

                                val initialOffsetX = if (fallInfo != null && !isSwapping) {
                                    (fallInfo.fromCol - fallInfo.toCol) * stride
                                } else {
                                    0f
                                }
                                val initialOffsetY = if (fallInfo != null && !isSwapping) {
                                    (fallInfo.fromRow - fallInfo.toRow) * stride
                                } else {
                                    0f
                                }
                                val targetOffsetX = remember(swapInfo, fallInfo) { Animatable(initialOffsetX) }
                                val targetOffsetY = remember(swapInfo, fallInfo) { Animatable(initialOffsetY) }

                                if (swapInfo != null) {
                                    val dx = (swapInfo.to.col - swapInfo.from.col) * stride
                                    val dy = (swapInfo.to.row - swapInfo.from.row) * stride
                                    LaunchedEffect(swapInfo) {
                                        targetOffsetX.snapTo(0f)
                                        targetOffsetY.snapTo(0f)
                                        val anim = tween<Float>(durationMillis = SWAP_DURATION_MS)
                                        coroutineScope {
                                            launch { targetOffsetX.animateTo(dx, anim) }
                                            launch { targetOffsetY.animateTo(dy, anim) }
                                        }

                                        if (completedSwapIds.add(cell.id) &&
                                            !swapCompletionDispatched.value &&
                                            completedSwapIds.containsAll(swappingCells.keys)
                                        ) {
                                            swapCompletionDispatched.value = true
                                            onSwapFinished()
                                        }
                                    }
                                } else if (fallInfo != null) {
                                    val dx = (fallInfo.fromCol - fallInfo.toCol) * stride
                                    val dy = (fallInfo.fromRow - fallInfo.toRow) * stride
                                    LaunchedEffect(fallingCells) {
                                        targetOffsetX.snapTo(dx)
                                        targetOffsetY.snapTo(dy)
                                        val anim = tween<Float>(durationMillis = FALL_STEP_DURATION_MS)
                                        coroutineScope {
                                            launch { targetOffsetX.animateTo(0f, anim) }
                                            launch { targetOffsetY.animateTo(0f, anim) }
                                        }
                                    }
                                } else {
                                    LaunchedEffect(Unit) {
                                        targetOffsetX.snapTo(0f)
                                        targetOffsetY.snapTo(0f)
                                    }
                                }

                                val vacuumInfo = swirlVacuumCells[cell.id]
                                val vacuumScale = if (vacuumInfo != null) {
                                    lerp(1f, 0.1f, swirlFlyProgress.value)
                                } else 1f
                                val vacuumAlpha = if (vacuumInfo != null) swirlFadeAlpha.value else 1f
                                val vacuumOffsetX = if (vacuumInfo != null) {
                                    (vacuumInfo.targetCol - vacuumInfo.fromCol) * stride * swirlFlyProgress.value
                                } else 0f
                                val vacuumOffsetY = if (vacuumInfo != null) {
                                    (vacuumInfo.targetRow - vacuumInfo.fromRow) * stride * swirlFlyProgress.value
                                } else 0f

                                // While teleporting, the entity's two halves are drawn by
                                // the teleport overlay (clipped to source/dest cells).
                                val isTeleporting = teleportingCells.containsKey(cell.id)
                                val effectiveAlpha = if (isTeleporting) 0f else vacuumAlpha

                                JellyItem(
                                    cell = cell,
                                    cellSize = cellDp,
                                    isSelected = isSelected,
                                    offsetX = (if (cell.isIceCube) 0f else targetOffsetX.value) + vacuumOffsetX,
                                    offsetY = (if (cell.isIceCube) 0f else targetOffsetY.value) + vacuumOffsetY,
                                    scale = vacuumScale,
                                    alpha = effectiveAlpha,
                                )
                            }
                        }
                    }
                },
                measurePolicy = { measurables, _ ->
                    val cellConstraints = Constraints.fixed(cellPx.roundToInt(), cellPx.roundToInt())
                    val placeables = measurables.map { it.measure(cellConstraints) }
                    layout(gridWpx.roundToInt(), gridHpx.roundToInt()) {
                        placeables.forEachIndexed { index, placeable ->
                            placeable.placeRelative(
                                x = (index % cols * stride).roundToInt(),
                                y = (index / cols * stride).roundToInt(),
                            )
                        }
                    }
                }
            )

            if (teleportingCells.isNotEmpty()) {
                val teleportProgress = remember(teleportingCells) { Animatable(0f) }
                LaunchedEffect(teleportingCells) {
                    teleportProgress.snapTo(0f)
                    teleportProgress.animateTo(
                        1f,
                        tween(durationMillis = EFFECTS_DURATION_MS),
                    )
                    onFallFinished()
                }
                val t = teleportProgress.value
                teleportingCells.forEach { (entityId, info) ->
                    val cell = state.grid.getOrNull(info.destRow)?.getOrNull(info.destCol) ?: return@forEach
                    // Source half: jelly slides downward and gets clipped by the source cell.
                    Box(
                        modifier = Modifier
                            .offset {
                                IntOffset(
                                    (info.sourceCol * stride).roundToInt(),
                                    (info.sourceRow * stride).roundToInt(),
                                )
                            }
                            .size(cellDp)
                            .clipToBounds(),
                    ) {
                        JellyItem(
                            cell = cell,
                            cellSize = cellDp,
                            isSelected = false,
                            offsetX = 0f,
                            offsetY = t * cellPx,
                            scale = 1f,
                            alpha = 1f,
                        )
                    }
                    // Destination half: jelly emerges from the top, clipped by the dest cell.
                    Box(
                        modifier = Modifier
                            .offset {
                                IntOffset(
                                    (info.destCol * stride).roundToInt(),
                                    (info.destRow * stride).roundToInt(),
                                )
                            }
                            .size(cellDp)
                            .clipToBounds(),
                    ) {
                        JellyItem(
                            cell = cell,
                            cellSize = cellDp,
                            isSelected = false,
                            offsetX = 0f,
                            offsetY = -(1f - t) * cellPx,
                            scale = 1f,
                            alpha = 1f,
                        )
                    }
                }
            }

            if (activatingLasers.isNotEmpty()) {
                val laserProgress = remember(activatingLasers) { Animatable(0f) }

                LaunchedEffect(activatingLasers) {
                    laserProgress.snapTo(0f)
                    laserProgress.animateTo(
                        1f,
                        tween(durationMillis = LASER_DURATION_MS),
                    )
                    onLaserFinished()
                }

                Canvas(modifier = Modifier.fillMaxSize()) {
                    val p = laserProgress.value
                    val beamAlpha = 0.4f + (1f - p) * 0.35f
                    for (laser in activatingLasers) {
                        val centerX = laser.origin.col * stride + cellPx / 2f
                        val centerY = laser.origin.row * stride + cellPx / 2f
                        when (laser.direction) {
                            LaserDirection.HORIZONTAL -> {
                                val left = centerX - centerX * p
                                val right = centerX + (size.width - centerX) * p
                                drawLine(
                                    color = Color(0xFFFFF59D).copy(alpha = beamAlpha),
                                    start = Offset(left, centerY),
                                    end = Offset(right, centerY),
                                    strokeWidth = cellPx * 0.26f,
                                    cap = StrokeCap.Round,
                                )
                                drawLine(
                                    color = Color.White.copy(alpha = beamAlpha),
                                    start = Offset(left, centerY),
                                    end = Offset(right, centerY),
                                    strokeWidth = cellPx * 0.11f,
                                    cap = StrokeCap.Round,
                                )
                            }

                            LaserDirection.VERTICAL -> {
                                val top = centerY - centerY * p
                                val bottom = centerY + (size.height - centerY) * p
                                drawLine(
                                    color = Color(0xFFFFF59D).copy(alpha = beamAlpha),
                                    start = Offset(centerX, top),
                                    end = Offset(centerX, bottom),
                                    strokeWidth = cellPx * 0.26f,
                                    cap = StrokeCap.Round,
                                )
                                drawLine(
                                    color = Color.White.copy(alpha = beamAlpha),
                                    start = Offset(centerX, top),
                                    end = Offset(centerX, bottom),
                                    strokeWidth = cellPx * 0.11f,
                                    cap = StrokeCap.Round,
                                )
                            }
                        }
                    }
                }
            }

            if (fusedBombs.isNotEmpty()) {
                val fuseProgress = remember(fusedBombs) { Animatable(0f) }

                LaunchedEffect(fusedBombs) {
                    fuseProgress.snapTo(0f)
                    fuseProgress.animateTo(
                        1f,
                        tween(durationMillis = FUSE_DURATION_MS),
                    )
                    onFuseFinished()
                }

                Canvas(modifier = Modifier.fillMaxSize()) {
                    val p = fuseProgress.value
                    // Pulsing highlight ring around each fused bomb
                    val pulse = 0.5f + 0.5f * p
                    for (bombPos in fusedBombs) {
                        val centerX = bombPos.col * stride + cellPx / 2f
                        val centerY = bombPos.row * stride + cellPx / 2f
                        drawCircle(
                            color = Color(0xFFFFAA00).copy(alpha = 0.7f * pulse),
                            radius = cellPx * 0.55f * pulse,
                            center = Offset(centerX, centerY),
                            style = Stroke(width = cellPx * 0.08f),
                        )
                    }
                }
            }

            if (explodingBombs.isNotEmpty()) {
                val isFullBoard = state.fullBoardExplosion
                val explosionProgress = remember(explodingBombs) { Animatable(0f) }

                LaunchedEffect(explodingBombs) {
                    explosionProgress.snapTo(0f)
                    explosionProgress.animateTo(
                        1f,
                        tween(durationMillis = if (isFullBoard) EFFECTS_DURATION_MS * 2 else EFFECTS_DURATION_MS),
                    )
                    onEffectsFinished()
                }

                Canvas(modifier = Modifier.fillMaxSize()) {
                    val p = explosionProgress.value
                    if (isFullBoard) {
                        val centerX = gridWpx / 2f
                        val centerY = gridHpx / 2f
                        val currentSize = p * max(gridWpx, gridHpx)
                        val alpha = 0.6f * (1f - p * 0.3f)
                        drawRect(
                            color = Color(0xFFFF2200).copy(alpha = alpha),
                            topLeft = Offset(
                                centerX - currentSize / 2,
                                centerY - currentSize / 2,
                            ),
                            size = Size(currentSize, currentSize),
                        )
                        drawRect(
                            color = Color.White.copy(alpha = alpha * 0.7f),
                            topLeft = Offset(
                                centerX - currentSize / 2,
                                centerY - currentSize / 2,
                            ),
                            size = Size(currentSize, currentSize),
                            style = Stroke(width = cellPx * 0.1f),
                        )
                    } else {
                        val singleSize = 3 * stride - gapPx
                        for (bombPos in explodingBombs) {
                            val centerX = bombPos.col * stride + cellPx / 2f
                            val centerY = bombPos.row * stride + cellPx / 2f
                            val currentSize = p * singleSize
                            val alpha = 0.55f * (1f - p * 0.4f)
                            drawRect(
                                color = Color(0xFFFF4400).copy(alpha = alpha),
                                topLeft = Offset(
                                    centerX - currentSize / 2,
                                    centerY - currentSize / 2,
                                ),
                                size = Size(currentSize, currentSize),
                            )
                            drawRect(
                                color = Color.White.copy(alpha = alpha * 0.6f),
                                topLeft = Offset(
                                    centerX - currentSize / 2,
                                    centerY - currentSize / 2,
                                ),
                                size = Size(currentSize, currentSize),
                                style = Stroke(width = cellPx * 0.06f),
                            )
                        }
                    }
                }
            }

            val currentDrag = dragState
            if (currentDrag != null) {
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val srcX = currentDrag.sourcePos.col * stride + cellPx / 2f
                    val srcY = currentDrag.sourcePos.row * stride + cellPx / 2f
                    val tgtX = currentDrag.targetPos.col * stride + cellPx / 2f
                    val tgtY = currentDrag.targetPos.row * stride + cellPx / 2f

                    val arrowColor = Color.White.copy(alpha = 0.85f)
                    val lineWidth = cellPx * 0.07f
                    val headSize = cellPx * 0.25f

                    drawLine(
                        color = arrowColor,
                        start = Offset(srcX, srcY),
                        end = Offset(tgtX, tgtY),
                        strokeWidth = lineWidth,
                        cap = StrokeCap.Round,
                    )

                    val headPath = Path().apply {
                        when (currentDrag.direction) {
                            DragDirection.RIGHT -> {
                                moveTo(tgtX + headSize * 0.5f, tgtY)
                                lineTo(tgtX - headSize * 0.5f, tgtY - headSize * 0.5f)
                                lineTo(tgtX - headSize * 0.5f, tgtY + headSize * 0.5f)
                            }
                            DragDirection.LEFT -> {
                                moveTo(tgtX - headSize * 0.5f, tgtY)
                                lineTo(tgtX + headSize * 0.5f, tgtY - headSize * 0.5f)
                                lineTo(tgtX + headSize * 0.5f, tgtY + headSize * 0.5f)
                            }
                            DragDirection.DOWN -> {
                                moveTo(tgtX, tgtY + headSize * 0.5f)
                                lineTo(tgtX - headSize * 0.5f, tgtY - headSize * 0.5f)
                                lineTo(tgtX + headSize * 0.5f, tgtY - headSize * 0.5f)
                            }
                            DragDirection.UP -> {
                                moveTo(tgtX, tgtY - headSize * 0.5f)
                                lineTo(tgtX - headSize * 0.5f, tgtY + headSize * 0.5f)
                                lineTo(tgtX + headSize * 0.5f, tgtY + headSize * 0.5f)
                            }
                        }
                        close()
                    }
                    drawPath(headPath, arrowColor)
                }
            }

            // Tutorial step-1 highlight (issue #110, F16.1): dims the board except the
            // hinted swap pair, drawn last so it sits on top of the pieces themselves.
            // The pair is always adjacent, so their union is a single rectangle —
            // "dimmed" is drawn as four bands around that rectangle rather than a true
            // cut-out, matching this file's existing no-blend-mode style.
            if (tutorial?.step == TutorialStep.SWAP_HINT && tutorial.highlightFrom != null && tutorial.highlightTo != null) {
                val from = tutorial.highlightFrom
                val to = tutorial.highlightTo
                Canvas(modifier = Modifier.fillMaxSize()) {
                    val unionLeft = min(from.col, to.col) * stride
                    val unionTop = min(from.row, to.row) * stride
                    val unionRight = (max(from.col, to.col) + 1) * stride
                    val unionBottom = (max(from.row, to.row) + 1) * stride
                    val scrimColor = Color.Black.copy(alpha = 0.55f)
                    drawRect(color = scrimColor, topLeft = Offset(0f, 0f), size = Size(gridWpx, unionTop))
                    drawRect(
                        color = scrimColor,
                        topLeft = Offset(0f, unionBottom),
                        size = Size(gridWpx, gridHpx - unionBottom),
                    )
                    drawRect(
                        color = scrimColor,
                        topLeft = Offset(0f, unionTop),
                        size = Size(unionLeft, unionBottom - unionTop),
                    )
                    drawRect(
                        color = scrimColor,
                        topLeft = Offset(unionRight, unionTop),
                        size = Size(gridWpx - unionRight, unionBottom - unionTop),
                    )
                    drawRect(
                        color = Color(0xFFFFD54F),
                        topLeft = Offset(unionLeft, unionTop),
                        size = Size(unionRight - unionLeft, unionBottom - unionTop),
                        style = Stroke(width = cellPx * 0.08f),
                    )
                }
            }
        }


        val fa = flightAnchors
        if (fa != null && (bombPowerUpFlight != null || goalJellyFlights.isNotEmpty() || familiarFlights.isNotEmpty())) {
            GameGridFlightOverlays(
                bombFlight = bombPowerUpFlight,
                goalFlights = goalJellyFlights,
                familiarFlights = familiarFlights,
                powerUps = powerUps,
                padding = padding,
                anchors = fa,
                cols = cols,
                rows = rows,
                onClearBombPowerUpFlight = onClearBombPowerUpFlight,
                onDismissGoalJellyFlight = onDismissGoalJellyFlight,
                onDismissFamiliarFlight = onDismissFamiliarFlight,
            )
        }
    }
}

